// Helper to handle 403 and redirect to login
import { handleForbidden } from "../Utils/logoutHelper";
import { MA_WATCHLIST_FAILED_MESSAGE } from "../Constants/AppConstants";
import {
  getWatchlistFailureMessage,
  normalizeWatchlistStatus,
} from "../Utils/maWatchlistStatusUtils";
import { toBackendFilterKeys } from "../Utils/filterKeyAliases";

const WATCHLIST_MUTATION_ERROR_FALLBACK =
  MA_WATCHLIST_FAILED_MESSAGE;

const buildWatchlistMutationError = (data = {}) => {
  const message = getWatchlistFailureMessage(
    data?.message,
    WATCHLIST_MUTATION_ERROR_FALLBACK
  );
  const error = new Error(message);

  error.watchlistStatus = normalizeWatchlistStatus(
    data?.status
    ?? data?.staus
    ?? data?.data?.status
    ?? data?.data?.watchlist?.status,
    ""
  );
  error.responseData = data;

  return error;
};

const normalizeWatchlistMutationResponse = (data = {}) => {
  const baseData = data && typeof data === "object" ? data : {};
  const payload =
    baseData?.data && typeof baseData.data === "object" ? baseData.data : {};
  const watchlist =
    payload?.watchlist && typeof payload.watchlist === "object"
      ? payload.watchlist
      : baseData?.watchlist ?? null;
  const status = normalizeWatchlistStatus(
    payload?.status
    ?? watchlist?.status
    ?? baseData?.status
    ?? baseData?.staus
  );

  return {
    ...baseData,
    ...payload,
    watchlist,
    status,
  };
};

export const loginUser = async (username, password) => {
  const response = await fetch(`${process.env.REACT_APP_API_URL}/user/login`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ username, password }),
  });

  if (response.status === 400) {
    throw new Error("Invalid username or password");
  }
  if (handleForbidden(response)) return;

  return response.json();
};

export const getInventory = async (token) => {
  
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/inventory/all`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Error fetching data");
    insertFrontendErrorLog({ responseBody: errorText || "Error fetching data" });
    throw new Error("Error fetching data");
  }

  return response.json();
};

export const fetchAccounts = async (token) => {
  
  const response = await fetch(`${process.env.REACT_APP_API_URL}/account`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Error fetching accounts");
    insertFrontendErrorLog({ responseBody: errorText || "Error fetching accounts" });
    throw new Error("Error fetching accounts");
  }

  return response.json();
};

export const createWatchlist = async (token, payload) => {
  insertFrontendLog({ payload });
  const response = await fetch(`${process.env.REACT_APP_API_URL}/create-watchlist`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (handleForbidden(response)) return;

  const data = await response.json().catch(() => null);

  if (!response.ok || data?.success === false) {
    const error = buildWatchlistMutationError(data);
    insertFrontendErrorLog({ responseBody: error.message });
    throw error;
  }

  const normalizedData = normalizeWatchlistMutationResponse(data);
  insertFrontendLog({ responseBody: normalizedData });
  return normalizedData;
};

export const updateWatchlist = async (token, watchlistId, payload) => {
  insertFrontendLog({ watchlistId, payload });
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/watchlist/modify/${watchlistId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    }
  );

  if (handleForbidden(response)) return;

  const data = await response.json().catch(() => null);

  if (!response.ok || data?.success === false) {
    const error = buildWatchlistMutationError(data);
    insertFrontendErrorLog({ responseBody: error.message });
    throw error;
  }

  const normalizedData = normalizeWatchlistMutationResponse(data);
  insertFrontendLog({ responseBody: normalizedData });
  return normalizedData;
};

export const clearWatchlist = async (token, watchlistId) => {
  insertFrontendLog({ watchlistId });
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/watchlist/clear/${watchlistId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) {
    insertFrontendErrorLog({ responseBody: "Unauthorized request" });
    throw new Error("Unauthorized request");
  }

  const data = await response.json();

  if (!response.ok || data?.success === false) {
    insertFrontendErrorLog({ responseBody: data?.message || "Failed to clear watchlist" });
    throw new Error(data?.message || "Failed to clear watchlist");
  }

  insertFrontendLog({ responseBody: data });
  return data;
};

export const deleteWatchlist = async (token, watchlistId) => {
  insertFrontendLog({ watchlistId });
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/watchlist/delete/${watchlistId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) return;

  const data = await response.json();

  if (!response.ok || data?.success === false) {
    insertFrontendErrorLog({ responseBody: data?.message || "Failed to delete watchlist" });
    throw new Error(data?.message || "Failed to delete watchlist");
  }

  insertFrontendLog({ responseBody: data });
  return data;
};

export const unsubscribeWatchlist = async (token, watchlistId) => {
  insertFrontendLog({ watchlistId });
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/watchlist/unsubscribe/${watchlistId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) {
    insertFrontendErrorLog({ responseBody: "Unauthorized request" });
    throw new Error("Unauthorized request");
  }

  const data = await response.json();

  if (!response.ok || data?.success === false) {
    insertFrontendErrorLog({ responseBody: data?.message || "Failed to unsubscribe watchlist" });
    throw new Error(data?.message || "Failed to unsubscribe watchlist");
  }

  insertFrontendLog({ responseBody: data });
  return data;
};

export const fetchWatchlistItems = async (
  token,
  page = 0,
  size = 7,
  sortBy = "cusip",
  sortDirection = "DESC",
  searchString = "",
  watchlistId = null,
  criteria = null
) => {
  const requestBody = {
    page,
    size,
    sortBy,
    sortDirection,
  };

  if (searchString) {
    requestBody.searchString = searchString;
  }

  if (watchlistId !== null && watchlistId !== undefined && watchlistId !== "") {
    requestBody.watchlistId = watchlistId;
  }

  if (criteria && typeof criteria === "object") {
    Object.assign(requestBody, criteria);
  }

  insertFrontendLog({ requestBody });

  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/watchlist-items`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
    }
  );

  if (handleForbidden(response)) return;

  const data = await response.json();

  if (!response.ok) {
    const errorMsg = "Something went wrong";
    insertFrontendErrorLog({ responseBody: errorMsg });
    throw new Error(errorMsg);
  }

  if (data?.success === false) {
    const errorMsg = data?.message || "Something went wrong";
    insertFrontendErrorLog({ responseBody: errorMsg });
    throw new Error(errorMsg);
  }

  insertFrontendLog({ responseBody: data });
  return data;
};

export const fetchWatchlists = async (
  token,
  page = 0,
  size = 7,
  sortBy = "id",
  sortDirection = "DESC"
) => {
  insertFrontendLog({ requestBody: { page, size, sortBy, sortDirection } });
  const params = new URLSearchParams({
    page: page.toString(),
    size: size.toString(),
    sortBy,
    sortDirection,
  });

  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/watchlists?${params.toString()}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) return;

  const data = await response.json();

  if (!response.ok || data?.success === false) {
    insertFrontendErrorLog({ responseBody: data?.message || "Failed to fetch watchlists" });
    throw new Error(data?.message || "Failed to fetch watchlists");
  }

  insertFrontendLog({ responseBody: data });
  return data;
};

export const fetchWatchlistDailyProcessStatus = async (token) => {
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/watchlist/daily-process-status`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) return;

  const data = await response.json().catch(() => null);

  if (!response.ok || data?.success === false) {
    const message = data?.message || "Failed to fetch watchlist daily process status";
    insertFrontendErrorLog({ responseBody: message });
    throw new Error(message);
  }

  insertFrontendLog({ responseBody: data });
  return data;
};

export const fetchSecMasterCusips = async (
  token,
  page = 0,
  size = 100,
  sortBy = "cusip",
  sortDirection = "DESC",
  searchString = "",
  watchlistId = null,
  criteria = null
) => {
  const requestBody = {
    page,
    size,
    sortBy,
    sortDirection,
  };

  if (searchString) {
    requestBody.searchString = searchString;
  }

  if (watchlistId !== null && watchlistId !== undefined && watchlistId !== "") {
    requestBody.watchlistId = watchlistId;
  }

  if (criteria && typeof criteria === "object") {
    Object.assign(requestBody, criteria);
  }

  insertFrontendLog({ requestBody });

  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/sec-master-cusips`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
    }
  );

  if (handleForbidden(response)) return;

  const data = await response.json();

  if (!response.ok) {
    const errorMsg = "Something went wrong";
    insertFrontendErrorLog({ responseBody: errorMsg });
    throw new Error(errorMsg);
  }

  if (data?.success === false) {
    const errorMsg = data?.message || "Something went wrong";
    insertFrontendErrorLog({ responseBody: errorMsg });
    throw new Error(errorMsg);
  }

  insertFrontendLog({ responseBody: data });
  return data;
};

export const fetchPaginatedInventory = async (token, page = 0, size = 1) => {
  insertFrontendLog({ requestBody: { page, size } });
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/inventory?page=${page}&size=${size}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Error fetching bonds");
    insertFrontendErrorLog({ responseBody: errorText || "Error fetching bonds" });
    throw new Error("Error fetching bonds");
  }

  return response.json();
};

export const checkCusipValidity = async (token, cusip, inventoryID) => {
  if (inventoryID) {
    insertFrontendLog({ requestBody: { bondInventoryId: inventoryID } });
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/inventory/bondInventoryId?bondInventoryId=${inventoryID}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (handleForbidden(response)) return;
    if (!response.ok) {
      const errorText = await response.text().catch(() => "Error fetching bonds");
      insertFrontendErrorLog({ responseBody: errorText || "Error fetching bonds" });
      throw new Error("Error fetching bonds");
    }

    return response.json();
  }
  insertFrontendLog({ requestBody: { cusip } });
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/inventory/cusip?cusip=${cusip}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Error fetching bonds");
    insertFrontendErrorLog({ responseBody: errorText || "Error fetching bonds" });
    throw new Error("Error fetching bonds");
  }

  return response;
};

export const getMyOrders = async (token, page = 0, size = 20, columnToSort = "updatedAt", sortingDirection = "desc") => { //key, asc/desc
  insertFrontendLog({ requestBody: { page, size, columnToSort, sortingDirection } });
  const response = await fetch(`${process.env.REACT_APP_API_URL}/order?page=${page}&size=${size}&columnToSort=${columnToSort}&sortingDirection=${sortingDirection}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Error fetching orders");
    insertFrontendErrorLog({ responseBody: errorText || "Error fetching orders" });
    throw new Error("Error fetching orders");
  }

  return response.json();
};

export const getStagingOrders = async (token, page = 0, size = 20, columnToSort = "updatedAt", sortingDirection = "desc") => {
  insertFrontendLog({ requestBody: { page, size, columnToSort, sortingDirection } });
  const response = await fetch(`${process.env.REACT_APP_API_URL}/staging-order?page=${page}&size=${size}&columnToSort=${columnToSort}&sortingDirection=${sortingDirection}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Error fetching staging orders");
    insertFrontendErrorLog({ responseBody: errorText || "Error fetching staging orders" });
    throw new Error("Error fetching staging orders");
  }

  return response.json();
};

export const placeOrder = async (token, orderData, timeoutRef = null) => {
  if (timeoutRef) {
    console.log("[orderentry] Clearing timeout before sending placeOrder request");
    clearTimeout(timeoutRef);
  }

  insertFrontendLog({ orderData });

  const response = await fetch(`${process.env.REACT_APP_API_URL}/order`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(orderData),
  });

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text();
    insertFrontendErrorLog({ responseBody: errorText || "Error placing order" });
    throw new Error("Error placing order");
  }

  const data = await response.json();

  // Check for success: false in response
  if (data.success === false) {
    // data.msg contain traceID
    insertFrontendErrorLog({ responseBody: data.message || "Error placing order" });
    throw new Error(data.message || "Error placing order");
  }

  insertFrontendLog({ responseBody: data });

  return data;
};

// Name-only save from the existing list view; HTTP 200 can still carry a validation failure.
export const saveListName = async (token, listId, listName) => {
  const response = await fetch(`${process.env.REACT_APP_API_URL}/order/list/${listId}/name`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ listName: listName?.trim() || null }),
  });
  if (handleForbidden(response)) throw new Error("Authentication required");
  const data = await response.json();
  if (!response.ok || data?.success !== true) {
    throw new Error(data?.message || "Unable to save list name.");
  }
  return data.data;
};

/**
 * Raises a Trumid List RFQ: one enquiry covering several bonds, sent as a single request.
 *
 * In plain terms: where placeOrder above sends one bond, this sends an array of them and
 * gets an array of orders back - one per bond, all sharing the enquiry.
 *
 * Copied from placeOrder deliberately: same headers, same forbidden handling, same
 * `success === false` check, same logging calls. Only the URL and the body shape differ.
 *
 * The success check matters more here than it looks. OrderController.createOrderList
 * answers a rejected list with HTTP 200 carrying {success:false, message} rather than a
 * 4xx - the same convention POST /order uses - so without this branch a refused list would
 * read as a success and the trader would never see why it was refused.
 *
 * @param {string} token
 * @param {object|Array<object>} orders shared list fields and items; legacy arrays remain accepted
 * @returns {Promise<Array<object>>} the created orders, one per bond
 */
export const placeListOrder = async (token, orders, timeoutRef = null) => {
  if (timeoutRef) {
    clearTimeout(timeoutRef);
  }

  insertFrontendLog({ orders });

  const response = await fetch(`${process.env.REACT_APP_API_URL}/order/list`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(orders),
  });

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text();
    insertFrontendErrorLog({ responseBody: errorText || "Error placing list RFQ" });
    throw new Error(errorText || "Error placing list RFQ");
  }

  const data = await response.json();

  if (data && data.success === false) {
    insertFrontendErrorLog({ responseBody: data.message || "Error placing list RFQ" });
    throw new Error(data.message || "Error placing list RFQ");
  }

  insertFrontendLog({ responseBody: data });

  return data;
};

/**
 * Cancels an ENTIRE List RFQ - every bond in it - given any one of its order ids.
 *
 * In plain terms: "pull the whole list", rather than cancelling bond by bond.
 *
 * Derived from cancelOrder below: same PUT shape, same headers, same bare-string body.
 * Differs only in the path - the backend refuses an order that is not part of a list,
 * because cancelOrder already handles a single-bond RFQ correctly.
 *
 * The body is the account identifier as a bare string, NOT JSON, even though the header
 * says application/json - the endpoint takes @RequestBody String accountId and Spring's
 * StringHttpMessageConverter reads it whatever the content type. The header is kept
 * identical to cancelOrder rather than "corrected" to text/plain, so both cancel calls
 * stay the same shape; text/plain was also verified to work against dev on 2026-09-10.
 */
export const cancelListOrder = async (token, orderId, accountId) => {
  insertFrontendLog({ orderId, accountId });

  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/order/list/cancel/${orderId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: accountId,
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text();
    insertFrontendErrorLog({ responseBody: errorText || "Error cancelling list RFQ" });
    throw new Error(errorText || "Error cancelling list RFQ");
  }

  const data = await response.json();

  if (data && data.success === false) {
    insertFrontendErrorLog({ responseBody: data.message || "Error cancelling list RFQ" });
    throw new Error(data.message || "Error cancelling list RFQ");
  }

  insertFrontendLog({ responseBody: data });

  return data;
};

/**
 * Every bond in the List RFQ that the given order belongs to.
 *
 * In plain terms: pass any one order id from a list and get back all the bonds in that list.
 *
 * Read-only counterpart to {@link cancelListOrder}, hitting the endpoint that shares its
 * definition of "the bonds in this list" (OrdersCommandHandler.getListRfqItems), so the
 * confirmation dialog cannot show a different set from the one the cancel would act on.
 *
 * Returns [] rather than throwing when the list cannot be read - the caller is a confirmation
 * dialog, and failing to enrich it must never stop the trader cancelling. The backend answers
 * a bad id with HTTP 200 and {success: false}, the same shape cancelListOrder handles.
 */
export const fetchListRfqItems = async (token, orderId) => {
  insertFrontendLog({ orderId });

  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/order/list/${orderId}/items`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (handleForbidden(response)) return [];
    if (!response.ok) {
      insertFrontendErrorLog({ responseBody: "Error fetching list RFQ items" });
      return [];
    }

    const data = await response.json();
    if (!Array.isArray(data)) {
      // {success:false, message} - a bad id, or an order that is not part of a list.
      insertFrontendErrorLog({ responseBody: data?.message || "No list items returned" });
      return [];
    }

    insertFrontendLog({ responseBody: data });
    return data;
  } catch (error) {
    insertFrontendErrorLog({ responseBody: error?.message || "Error fetching list RFQ items" });
    return [];
  }
};

/**
 * Canonical filter keys for POST /inventory/filter.
 *
 * BondsController.getBondFilter reads only the API-side spelling of each pair. The UI carries
 * its own spelling on the same object (and saved characteristics store it too), so both end up
 * travelling together. Frontend state deliberately keeps both — this is a wire-level concern:
 * resolve each pair to the key the backend actually reads, then drop the alias.
 *
 * canonical (what the backend reads) -> alias (UI spelling, dropped)
 */
/**
 * Read under neither spelling. applyInventoryFilters already derives minRatingSP /
 * minRatingMoody from these using bond-type rules, so drop rather than promote — promoting
 * would apply a rating floor the UI deliberately did not set (e.g. on Treasury).
 */
const FILTER_KEYS_NOT_READ_BY_BACKEND = ["minimumRating", "minimumRatingMoody"];

const withCanonicalFilterKeys = (filter) => {
  if (!filter || typeof filter !== "object") return filter;

  const hasValue = (v) => v !== undefined && v !== null && v !== "";
  // Shared with the characteristic/ladder submit payloads — same pairs, same winner
  const out = toBackendFilterKeys(filter);

  // "call" is read only as a fallback for "callable" (BondsController:157-158). Wire-only:
  // a stored characteristics JSON keeps "callable" on its own, and re-adding "call" there
  // would render a duplicate "Callable" chip.
  const callValue = [out.callable, out.call].find(hasValue);
  delete out.call;
  if (hasValue(callValue)) out.callable = callValue;
  else delete out.callable;

  FILTER_KEYS_NOT_READ_BY_BACKEND.forEach((key) => delete out[key]);
  return out;
};

export const fetchFilteredInventory = async (token, filter) => {
  const payload = withCanonicalFilterKeys(filter);
  insertFrontendLog({ filter: payload });
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/inventory/filter`,
    {
      method: "POST",
      body: JSON.stringify(payload),
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text();
    insertFrontendErrorLog({ responseBody: errorText || "Error fetching criteria count" });
    throw new Error("Error fetching criteria count");
  }

  const data = await response.json();
  insertFrontendLog({ responseBody: data });
  return data;
};

export const fetchFilterInitialValues = async (token, payload) => {
  // No canonicalization here: getDefaultValues reads only "type"
  insertFrontendLog({ payload });
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/inventory/default-filter-values`,
    {
      method: "POST",
      body: JSON.stringify(payload),
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text();
    insertFrontendErrorLog({ responseBody: errorText || "Error fetching Inital values" });
    throw new Error("Error fetching Inital values");
  }

  const data = await response.json();
  insertFrontendLog({ responseBody: data });
  return data;
};

export const fetchWatchlistDefaultFilterValues = async (token, payload) => {
  insertFrontendLog({ payload });
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/watchlist/default-filter-values`,
    {
      method: "POST",
      body: JSON.stringify(payload),
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Error fetching watchlist filter defaults");
    insertFrontendErrorLog({ responseBody: errorText || "Error fetching watchlist filter defaults" });
    throw new Error("Error fetching watchlist filter defaults");
  }

  const data = await response.json();
  insertFrontendLog({ responseBody: data });
  return data;
};

export const fetchWatchlistFilterOptions = async (token) => {
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/watchlist/get-filter-options`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Error fetching watchlist sectors");
    insertFrontendErrorLog({ responseBody: errorText || "Error fetching watchlist sectors" });
    throw new Error("Error fetching watchlist sectors");
  }

  const data = await response.json();
  insertFrontendLog({ responseBody: data });
  return data;
};

export const fetchWatchlistGovStateOptions = async (token) => {
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/watchlist/gov-state-list`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(
      () => "Error fetching watchlist state options"
    );
    insertFrontendErrorLog({
      responseBody: errorText || "Error fetching watchlist state options",
    });
    throw new Error("Error fetching watchlist state options");
  }

  const data = await response.json();
  insertFrontendLog({ responseBody: data });
  return data;
};

export const fetchWatchlistSnpRatingOptions = async (token) => {
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/watchlist/SPRatings-list`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(
      () => "Error fetching watchlist S&P rating options"
    );
    insertFrontendErrorLog({
      responseBody: errorText || "Error fetching watchlist S&P rating options",
    });
    throw new Error("Error fetching watchlist S&P rating options");
  }

  const data = await response.json();
  insertFrontendLog({ responseBody: data });
  return data;
};

export const fetchWatchlistMoodyRatingOptions = async (token) => {
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/watchlist/MoodyRatings-list`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(
      () => "Error fetching watchlist Moody's rating options"
    );
    insertFrontendErrorLog({
      responseBody: errorText || "Error fetching watchlist Moody's rating options",
    });
    throw new Error("Error fetching watchlist Moody's rating options");
  }

  const data = await response.json();
  insertFrontendLog({ responseBody: data });
  return data;
};

export const createBondLadder = async (token, bondLadderData, timeoutRef = null) => {
  if (timeoutRef) {
    console.log("[orderentry] Clearing timeout before sending createBondLadder request");
    clearTimeout(timeoutRef);
  }

  insertFrontendLog({ bondLadderData });

  const response = await fetch(`${process.env.REACT_APP_API_URL}/bond-ladder`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(bondLadderData),
  });

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text();
    insertFrontendErrorLog({ responseBody: errorText || "Error creating bond ladder" });
    throw new Error("Error creating bond ladder");
  }

  const data = await response.json();

  // Check for success: false in response
  if (data.success === false) {
    insertFrontendErrorLog({ responseBody: data.message || "Error creating bond ladder" });
    throw new Error(data.message || "Error creating bond ladder");
  }

  insertFrontendLog({ responseBody: data });

  return data;
};

export const createBondCharacteristic = async (
  token,
  bondCharacteristicData,
  timeoutRef = null
) => {
  if (timeoutRef) {
    console.log("[orderentry] Clearing timeout before sending createBondCharacteristic request");
    clearTimeout(timeoutRef);
  }

  insertFrontendLog({ bondCharacteristicData });

  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/bond-characteristics`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(bondCharacteristicData),
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text();
    insertFrontendErrorLog({ responseBody: errorText || "Error creating bond characteristic" });
    throw new Error("Error creating bond characteristic");
  }

  const data = await response.json();

  // Check for success: false in response
  if (data.success === false) {
    insertFrontendErrorLog({ responseBody: data.message || "Error creating bond characteristic" });
    throw new Error(data.message || "Error creating bond characteristic");
  }

  insertFrontendLog({ responseBody: data });

  return data;
};

export const getBondCharacteristics = async (token, page = 0, size = 20, columnToSort = "characteristicMstId", sortingDirection = "desc") => {
  insertFrontendLog({ requestBody: { page, size, columnToSort, sortingDirection } });
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/bond-characteristics?page=${page}&size=${size}&columnToSort=${columnToSort}&sortingDirection=${sortingDirection}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Error fetching bond characteristics");
    insertFrontendErrorLog({ responseBody: errorText || "Error fetching bond characteristics" });
    throw new Error("Error fetching bond characteristics");
  }

  return response.json();
};

export const getBondLadder = async (token, page = 0, size = 20, columnToSort = "bondLadderId", sortingDirection = "desc") => {
  insertFrontendLog({ requestBody: { page, size, columnToSort, sortingDirection } });
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/bond-ladder?page=${page}&size=${size}&columnToSort=${columnToSort}&sortingDirection=${sortingDirection}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Error fetching bond ladder");
    insertFrontendErrorLog({ responseBody: errorText || "Error fetching bond ladder" });
    throw new Error("Error fetching bond ladder");
  }

  return response.json();
};

export const getBondCharacteristicById = async (token, characteristicId) => {
  insertFrontendLog({ requestBody: { characteristicId } });
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/bond-characteristics/${characteristicId}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Error fetching bond characteristic details");
    insertFrontendErrorLog({ responseBody: errorText || "Error fetching bond characteristic details" });
    throw new Error("Error fetching bond characteristic details");
  }

  return response.json();
};

export const getBondLadderById = async (token, ladderId) => {
  insertFrontendLog({ requestBody: { ladderId } });
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/bond-ladder/${ladderId}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Error fetching bond ladder details");
    insertFrontendErrorLog({ responseBody: errorText || "Error fetching bond ladder details" });
    throw new Error("Error fetching bond ladder details");
  }

  return response.json();
};

// PENDING characteristics on a ladder, each paired with its rung.
// Backs the "View Characteristics" action on the ladder blotter.
export const getPendingCharacteristicsByLadderId = async (token, ladderId) => {
  insertFrontendLog({ requestBody: { ladderId } });
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/bond-ladder/${ladderId}/pending-characteristics`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Error fetching ladder characteristics");
    insertFrontendErrorLog({ responseBody: errorText || "Error fetching ladder characteristics" });
    throw new Error("Error fetching ladder characteristics");
  }

  return response.json();
};

export const getSavedFilters = async (token) => {
  
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/saved-filters`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Error fetching filters");
    insertFrontendErrorLog({ responseBody: errorText || "Error fetching filters" });
    throw new Error("Error fetching filters");
  }

  return response.json();
};

export const saveFilter = async (token, filterData) => {
  /**
     * eg payload
     * {
          "filterName": "my_filter4",
          "savedFilterJson": "{\"call\": \"Yes\"}"
        }
     * 
     */
  //log token and filterdat
  insertFrontendLog({ filterData });

  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/saved-filters`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(filterData),
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text();
    insertFrontendErrorLog({ responseBody: errorText || "Error saving filter" });
    throw new Error("Error saving filter");
  }

  const data = await response.json();
  insertFrontendLog({ responseBody: data });
  return data;
};

export const updateFilter = async (token, savedFiltersId, filterData) => {
  insertFrontendLog({ filterData });
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/saved-filters/update/${savedFiltersId}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(filterData),
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text();
    insertFrontendErrorLog({ responseBody: errorText || "Error updating filter" });
    throw new Error("Error updating filter");
  }

  const data = await response.json();
  insertFrontendLog({ responseBody: data });
  return data;
};

export const deleteFilter = async (token, savedFiltersId, filterName) => {
  insertFrontendLog({ savedFiltersId, filterName });
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/saved-filters/delete/${savedFiltersId}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text();
    insertFrontendErrorLog({ responseBody: errorText || "Error deleting filter" });
    throw new Error("Error deleting filter");
  }

  const data = await response.json();
  insertFrontendLog({ responseBody: data });
  return data;
};

export const editOrder = async (token, orderData, orderId, timeoutRef = null) => {
  if (timeoutRef) {
    console.log("[orderentry] Clearing timeout before sending editOrder request");
    clearTimeout(timeoutRef);
  }

  insertFrontendLog({ orderData, orderId });

  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/order/modify/${orderId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(orderData),
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text();
    insertFrontendErrorLog({ responseBody: errorText || "Error updating order" });
    throw new Error("Error updating order");
  }

  const data = await response.json();

  // Check for success: false in response
  if (data.success === false) {
    insertFrontendErrorLog({ responseBody: data.message || "Error updating order" });
    throw new Error(data.message || "Error updating order");
  }

  insertFrontendLog({ responseBody: data });
  return data;
};

export const fetchPMUsers = async (token) => {
  
  const response = await fetch(`${process.env.REACT_APP_API_URL}/user/pm`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Error fetching PM users");
    insertFrontendErrorLog({ responseBody: errorText || "Error fetching PM users" });
    throw new Error("Error fetching PM users");
  }

  return response.json();
  /**
   * example return
   * [ {
        "userId": 1,
        "email": "raj.shah@fintechglobal.center",
        "username": "rajPM",
        "role": "PM"
    },
    {
        "userId": 2,
        "email": "suresh.shah@fintechglobal.center",
        "username": "sureshPM",
        "role": "PM"
    },..
]
   */
};

export const fetchPMAccounts = async (token, pmId) => {
  insertFrontendLog({ requestBody: { pmId } });
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/account/pm/${pmId}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Error fetching PM accounts");
    insertFrontendErrorLog({ responseBody: errorText || "Error fetching PM accounts" });
    throw new Error("Error fetching PM accounts");
  }
  /**
 * sample resp
 *  [
    {
        "accountId": 1,
        "pmId": 1,
        "accountIdentifier": "ACC-202301",
        "status": "ACTIVE"
    },
    {
        "accountId": 28,
        "pmId": 1,
        "accountIdentifier": "ACC-202302",
        "status": "ACTIVE"
    },
    {
        "accountId": 29,
        "pmId": 1,
        "accountIdentifier": "ACC-202303",
        "status": "ACTIVE"
    }
]
 */
  return response.json();
};

// validateToken intentionally does NOT redirect on 403
export const validateToken = async (token) => {
  try {
    
    const response = await fetch(`${process.env.REACT_APP_API_URL}/user/validate-token`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) { // also covers 404/forbidden
      const errorText = await response.text().catch(() => "Invalid token");
      insertFrontendErrorLog({ responseBody: errorText || "Invalid token" });
      return false;
    }
    const data = await response.json();
    return data.message === "Valid";
  } catch (error) {
    return false;
  }
};

export const getUserProfile = async (token) => {
  
  const response = await fetch(`${process.env.REACT_APP_API_URL}/user/profile`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Error fetching user profile");
    insertFrontendErrorLog({ responseBody: errorText || "Error fetching user profile" });
    throw new Error("Error fetching user profile");
  }

  return response.json();
};

export const placeBulkOrders = async (token, orders) => {
  insertFrontendLog({ orders });
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/ladder-orders`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(orders),
    }
  );

  if (handleForbidden(response)) return;
  if (response.status === 400) {
    insertFrontendErrorLog( { responseBody: "Something went wrong." });
    throw new Error("Something went wrong.");
  }
  if (!response.ok) {
    const errorText = await response.text();
    insertFrontendErrorLog( { responseBody: errorText || response.status });
    throw new Error(`Error placing bulk orders: ${errorText}`);
  }

  const data = await response.json();

  // Check for success: false in response
  if (data.success === false) {
    insertFrontendErrorLog( { responseBody: data.message || "Error placing bulk orders" });
    throw new Error(data.message || "Error placing bulk orders");
  }

  insertFrontendLog({ responseBody: data });

  return data;
};

export const getLiveMarketdataItems = async (
  token,
  page = 0,
  size = 10,
  sortBy = "maturityDate",
  sortDirection = "DESC",
  currentWatchlistId = null,
  traceId = null,
  previousWatchlistId = null
) => {
  insertFrontendLog({ requestBody: { page, size, sortBy, sortDirection, currentWatchlistId, traceId, previousWatchlistId } });

  const paramsObj = {
    page: page.toString(),
    size: size.toString(),
    sortBy,
    sortDirection,
    currentWatchlistId:
      currentWatchlistId !== null && currentWatchlistId !== undefined && currentWatchlistId !== ""
        ? String(currentWatchlistId)
        : "",
  };

  if (traceId) {
    paramsObj.traceId = String(traceId);
  }

  if (previousWatchlistId !== null && previousWatchlistId !== undefined && previousWatchlistId !== "") {
    paramsObj.previousWatchlistId = String(previousWatchlistId);
  }

  const params = new URLSearchParams(paramsObj);

  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/live-marketdata-items?${params.toString()}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) return;

  const data = await response.json();

  if (!response.ok) {
    insertFrontendErrorLog({ responseBody: data?.message || "Failed to fetch live marketdata items" });
    throw new Error(data?.message || "Failed to fetch live marketdata items");
  }

  if (data?.success === false) {
    const processingError = new Error(
      data?.message || "Live market data request is still processing"
    );
    processingError.isMarketDataProcessing = true;
    processingError.responseData = data;
    insertFrontendLog({ responseBody: data });
    throw processingError;
  }

  insertFrontendLog({ responseBody: data });
  return data;
};

export const fetchMarketDataDetail = async (token, identifier) => {
  const params = new URLSearchParams({ identifier });
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/marketdata-detail?${params.toString()}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) return;

  const data = await response.json();

  if (!response.ok || data?.success === false) {
    throw new Error(data?.message || "Failed to fetch market data detail");
  }

  return data;
};

export const modifyOrderQuote = async (token, quote, action) => {
  // Prepare request body with the entire quote and the action
  const requestBody = {
    action,
    ...quote
  };

  insertFrontendLog({ requestBody });

  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/order-quotes/modify/${quote.orderQuotesId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorData = await response.json();
    insertFrontendErrorLog({ responseBody: errorData.message || "Error modifying quote" });
    throw new Error(errorData.message || "Error modifying quote");
  }

  const data = await response.json();
  insertFrontendLog({ responseBody: data });
  return data;
};

export const fetchDealers = async (token, venueParam) => {
  insertFrontendLog({ requestBody: { venue: venueParam } });
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/dealer?venue=${venueParam}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Error fetching dealers");
    insertFrontendErrorLog({ responseBody: errorText || "Error fetching dealers" });
    throw new Error("Error fetching dealers");
  }

  return response.json();
};

const getBondCalcLocalTimestamp = () => {
  const now = new Date();
  const pad = (value, length = 2) => String(value).padStart(length, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}.${pad(now.getMilliseconds(), 3)}`;
};

const normalizeBondCalcPayload = (response, fallbackUUID) => {
  if (!response) return { UUID: fallbackUUID };

  const raw = response.analyticsData || response;

  const settlementDate = raw?.Settlement_Date ?? raw?.settlementDate;
  const accruedInterest = raw?.Accr_Interest ?? raw?.Accrued_Interest ?? raw?.accrInterest;
  const duration = raw?.Duration ?? raw?.duration;
  const yieldValue = raw?.Yield ?? raw?.yield;
  const yieldToWorst = raw?.YieldToWorst ?? raw?.Yield_To_Worst ?? raw?.yieldToWorst;
  const yieldValuePP = raw?.YieldValuePP ?? raw?.Yield_Value_PP ?? raw?.yieldValuePP;

  return {
    ...raw,
    UUID: raw?.UUID || response?.UUID || fallbackUUID,
    Settlement_Date: settlementDate,
    Accr_Interest: accruedInterest,
    Accrued_Interest: accruedInterest,
    Duration: duration,
    Yield: yieldValue,
    YieldToWorst: yieldToWorst,
    Yield_To_Worst: yieldToWorst,
    YieldValuePP: yieldValuePP,
    Yield_Value_PP: yieldValuePP,
    Macaulay_Duration: raw?.Macaulay_Duration ?? raw?.macaulayDuration,
    Modified_Duration: raw?.Modified_Duration ?? raw?.modifiedDuration,
    Modified_Convexity: raw?.Modified_Convexity ?? raw?.modifiedConvexity,
    Dollar_Convexity: raw?.Dollar_Convexity ?? raw?.dollarConvexity,
    DV01: raw?.DV01 ?? raw?.dv01,
  };
};

export const sendBondCalculation = async (token, calcData) => {
  const bondCalcPath = "/analytics/price-cusip";
  //overriding the userid to 0 so in backend lambda is called
  calcData.userId = 0;
  console.log(
    "debugging-bond-calc",
    `[${getBondCalcLocalTimestamp()}] request-sent /bond-calc${bondCalcPath}`,
    {
      UUID: calcData?.UUID,
      cusip: calcData?.cusip,
      price: calcData?.price,
      userId: calcData?.userId,
      tradeDate: calcData?.tradeDate,
    }
  );

  // Deprecated endpoint (kept commented intentionally):
  // const response = await fetch(
  //   `${process.env.REACT_APP_API_URL}/bond-calc/send`,
  //   {
  //     method: "POST",
  //     headers: {
  //       "Content-Type": "application/json",
  //       Authorization: `Bearer ${token}`,
  //     },
  //     body: JSON.stringify(calcData),
  //   }
  // );
  insertFrontendLog({ calcData });
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/bond-calc${bondCalcPath}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(calcData),
    }
  );

  if (handleForbidden(response)) return;
  const data = await response.json();

  const normalized = normalizeBondCalcPayload(data, calcData?.UUID);

  if (!response.ok) {
    insertFrontendErrorLog({ responseBody: data || response.status });
    const errorPayload = { error: true, ...data, UUID: calcData?.UUID };
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("bondCalcError", { detail: errorPayload }));
    }
    return errorPayload;
  }

  if (data?.success === false) {
    insertFrontendErrorLog({ responseBody: data.message || "Bond calculation failed" });
    const errorPayload = { error: true, ...data, UUID: calcData?.UUID };
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("bondCalcError", { detail: errorPayload }));
    }
    return errorPayload;
  }

  insertFrontendLog({ responseBody: normalized });

  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("bondCalcUpdate", { detail: normalized }));
  }

  return normalized;
};

export const fetchPriceCusipAnalytics = async (token, calcData) => {
  return sendBondCalculation(token, calcData);
};

const getCurrentUTCDateString = () => {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
    .toISOString()
    .split("T")[0];
};

const normalizeSettlementDateString = (dateValue) => {
  if (!dateValue) return "";

  if (dateValue instanceof Date) {
    if (Number.isNaN(dateValue.getTime())) return "";
    return dateValue.toISOString().split("T")[0];
  }

  const valueStr = String(dateValue).trim();
  let match;
  const ymd = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:[T\s].*)?$/;
  const ymdCompact = /^(\d{4})(\d{2})(\d{2})$/;
  const mdY = /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[T\s].*)?$/;

  if ((match = valueStr.match(ymd)) || (match = valueStr.match(ymdCompact))) {
    const yyyy = match[1];
    const mm = String(Number(match[2])).padStart(2, "0");
    const dd = String(Number(match[3])).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }

  if ((match = valueStr.match(mdY))) {
    const yyyy = match[3];
    const mm = String(Number(match[1])).padStart(2, "0");
    const dd = String(Number(match[2])).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }

  const normalized = /[zZ]|[+-]\d{2}:?\d{2}$/.test(valueStr) ? valueStr : `${valueStr}Z`;
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? "" : parsed.toISOString().split("T")[0];
};

const extractSettlementDate = (payload) => {
  if (!payload) return "";

  if (typeof payload === "string") {
    const trimmed = payload.replace(/^"|"$/g, "").trim();

    try {
      const parsed = JSON.parse(trimmed);
      return extractSettlementDate(parsed);
    } catch {
      return trimmed;
    }
  }

  if (typeof payload === "object") {
    return (
      payload.Settlement_Date ??
      payload.settlementDate ??
      payload.settlement_date ??
      payload.analyticsData?.Settlement_Date ??
      payload.analyticsData?.settlementDate ??
      ""
    );
  }

  return "";
};

export const fetchBondCalcSettlementDate = async (token) => {
  const fallbackSettlementDate = getCurrentUTCDateString();

  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/bond-calc/settlement-date`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (handleForbidden(response)) return fallbackSettlementDate;
    if (!response.ok) {
      const errorText = await response.text().catch(() => "Error fetching settlement date");
      insertFrontendErrorLog({ responseBody: errorText || "Error fetching settlement date" });
      return fallbackSettlementDate;
    }

    const text = await response.text();
    const settlementDate = normalizeSettlementDateString(extractSettlementDate(text));
    return settlementDate || fallbackSettlementDate;
  } catch (error) {
    insertFrontendErrorLog({ responseBody: error?.message || "Error fetching settlement date" });
    return fallbackSettlementDate;
  }
};

export const updateBondCharacteristicStatus = async (token, characteristicId, additional_comments = "") => {
  const payload = {
    action: 1,
    additional_comments: additional_comments ?? "",
  };
  insertFrontendLog({ payload, characteristicId });

  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/bond-characteristics/modify/${characteristicId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text();
    insertFrontendErrorLog({ responseBody: errorText || "Error updating characteristic status" });
    throw new Error("Error updating characteristic status");
  }

  const data = await response.json();

  // Check for success: false in response
  if (data.success === false) {
    insertFrontendErrorLog({ responseBody: data.message || "Error updating characteristic status" });
    throw new Error(data.message || "Error updating characteristic status");
  }

  insertFrontendLog({ responseBody: data });
  return data;
};

export const updateBondLadderStatus = async (token, bondLadderId, comment = "") => {
  if (!bondLadderId) throw new Error("Bond Ladder ID is required");

  const ladderData = {
    bondLadderId,
    action: 1,
    additional_comments: comment ?? "",
  };

  return await updateBondLadder(token, ladderData);
};

export const updateBondLadder = async (token, ladderData) => {
  try {
    insertFrontendLog({ ladderData });
    const response = await fetch(`${process.env.REACT_APP_API_URL}/bond-ladder/modify/${ladderData.bondLadderId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(ladderData),
    });

    if (handleForbidden(response)) return;
    if (!response.ok) {
      const errorData = await response.json();
      insertFrontendErrorLog({ responseBody: errorData.message || 'Failed to update bond ladder' });
      throw new Error(errorData.message || 'Failed to update bond ladder');
    }

    const data = await response.json();

    // Check for success: false in response
    if (data.success === false) {
      insertFrontendErrorLog({ responseBody: data.message || "Failed to update bond ladder" });
      throw new Error(data.message || "Failed to update bond ladder");
    }

    insertFrontendLog({ responseBody: data });
    return data;
  } catch (error) {
    console.error('Error updating bond ladder:', error);
    throw error;
  }
};

export const fetchIssuers = async (token) => {
  
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/inventory/issuers`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Error fetching issuers");
    insertFrontendErrorLog({ responseBody: errorText || "Error fetching issuers" });
    throw new Error("Error fetching issuers");
  }

  return response.json();
};

export const fetchBrokerDealers = async (token) => {
  
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/inventory/broker-dealer`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Error fetching broker dealers");
    insertFrontendErrorLog({ responseBody: errorText || "Error fetching broker dealers" });
    throw new Error("Error fetching broker dealers");
  }

  return response.json();
};

export const fetchCounterparties = async (token, page = 0, size = 10000, columnToSort = "counterPartyName", sortingDirection = "asc") => {
  const params = new URLSearchParams({
    page: page.toString(),
    size: size.toString(),
    columnToSort,
    sortingDirection,
  });
  insertFrontendLog({ requestBody: { page, size, columnToSort, sortingDirection } });
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/counter-parties?${params.toString()}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const message = errorData?.message || "Error fetching counterparties";
    insertFrontendErrorLog({ responseBody: message });
    throw new Error(message);
  }

  // Now returns a paginated payload with { content, totalElements, ... }
  return response.json();
};

export const fetchActiveAccounts = async (token, page = 0, size = 20, userID = 0, columnToSort = "updatedAt", sortingDirection = "default") => {
  // try {
  const params = new URLSearchParams({
    page: page.toString(),
    size: size.toString(),
    userId: userID != null ? userID.toString() : null,
    columnToSort,
    sortingDirection
  });
  insertFrontendLog({ requestBody: { page, size, userId: userID, columnToSort, sortingDirection } });
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/account/active-account?${params.toString()}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Error fetching active accounts");
    insertFrontendErrorLog({ responseBody: errorText || "Error fetching active accounts" });
    throw new Error("Error fetching active accounts");
  }

  return response.json();
};

export const fetchCustodians = async (token) => {
  
  const response = await fetch(`${process.env.REACT_APP_API_URL}/custodian`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Error fetching custodians");
    insertFrontendErrorLog({ responseBody: errorText || "Error fetching custodians" });
    throw new Error("Error fetching custodians");
  }

  return response.json();
};

export const validateSecurityCusip = async (token, cusip) => {
  insertFrontendLog({ requestBody: { cusip } });
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/inventory/validate-identifier?identifier=${cusip}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) return;
  const data = await response.json();

  // Handle both error cases: non-200 and 200 with success: false
  if (!response.ok || data.success === false) {
    insertFrontendErrorLog({ responseBody: data.message || "Invalid CUSIP" });
    return { success: false, message: data.message || "Invalid CUSIP" };
  }

  return {
    success: true,
    valid: true,
    ...data,
    secMasterDTO: data.secMasterDTO || {}
  };
};

export const createDemoAccountPosition = async (token, accountData) => {
  const response = await fetch(`${process.env.REACT_APP_API_URL}/account-position-mapping`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(accountData),
  });

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Error creating demo account");
  }

  return response.json();
};

export const deleteAccountById = async (token, accountId) => {
  insertFrontendLog({ accountId });
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/account/delete/${accountId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorData = await response.json();
    insertFrontendErrorLog( { responseBody: errorData.message || "Error deleting account" });
    throw new Error(errorData.message || "Error deleting account");
  }

  const data = await response.json();
  insertFrontendLog({ responseBody: data });
  return data;
};
export const deleteAccountPostionMapping = async (token, mappingId) => {
  insertFrontendLog({ mappingId });
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/account-position-mapping/delete/${mappingId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorData = await response.json();
    insertFrontendErrorLog({ responseBody: errorData.message || "Error deleting account" });
    throw new Error(errorData.message || "Error deleting account");
  }

  const data = await response.json();
  insertFrontendLog({ responseBody: data });
  return data;
};

export const fetchAccountPositions = async (token, accountId, bondType = null, page = 0, size = 20, columnToSort = "updatedAt", sortingDirection = "desc", securityCharge = null) => {

  let url = `${process.env.REACT_APP_API_URL}/account-position-mapping?accountId=${accountId}`;

  if (bondType && bondType !== "All") {
    url += `&bondType=${encodeURIComponent(bondType)}`;
  }

  if (securityCharge !== null && securityCharge !== undefined && securityCharge !== "" && securityCharge !== "All") {
    url += `&securityCharge=${encodeURIComponent(securityCharge)}`;
  }

  url += `&page=${page}&size=${size}&columnToSort=${columnToSort}&sortingDirection=${sortingDirection}`;
  insertFrontendLog({ requestBody: { accountId, bondType, securityCharge, page, size, columnToSort, sortingDirection } });
  const response = await fetch(
    url,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Error fetching account positions");
    insertFrontendErrorLog({ responseBody: errorText || "Error fetching account positions" });
    throw new Error("Error fetching account positions");
  }

  return response.json();
};

export const recalculateAccountPosition = async (token, accountPositionMappingId) => {
  insertFrontendLog({ accountPositionMappingId });
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/account-position-mapping/re-calc/${accountPositionMappingId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    // We can ignore errors here since this is a background update
    console.warn(`Failed to recalculate position ${accountPositionMappingId}`);
    insertFrontendErrorLog({ responseBody: `Failed to recalculate position ${accountPositionMappingId}` });
    return false;
  }

  insertFrontendLog({ responseBody: "Success recalculateAccountPosition" });
  return true;
};

export const fetchSecurityInfo = async (token, cusip) => {
  insertFrontendLog({ requestBody: { cusip } });
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/inventory/get-bond-details?cusip=${cusip}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );
  if (handleForbidden(response)) return;
  const data = await response.json();

  // Handle success: false even if status is 200
  if (data.success === false) {
    // Return an object with error and message for UI to display
    insertFrontendErrorLog({ responseBody: data.message || "No details found for bond." });
    return { error: true, message: data.message || "No details found for bond." };
  }

  return data;
};

export const getBondTypeOptions = async (token) => {
  
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/inventory/distinct-bond-types`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Error fetching bond types");
    insertFrontendErrorLog({ responseBody: errorText || "Error fetching bond types" });
    throw new Error("Error fetching bond types");
  }
  // it will be array of strings like ["Corporate", "Municipal", "Treasury"] return them normalised meaning first letter capitalized
  const data = await response.json();
  const normalizedData = data.map(type => type.charAt(0).toUpperCase()
    + type.slice(1).toLowerCase());
  // Return the normalized array
  return normalizedData;
}

export const getSecurityChargeOptions = async (token) => {
  const fetchOptions = async (path) => {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}${path}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (handleForbidden(response)) return;
    return response;
  };

  let response = await fetchOptions("/inventory/distinct-security-charges");

  // The backend route may be exposed without the inventory prefix. Keep this
  // fallback narrow so older/local API shapes can still serve the dropdown.
  if (response && response.status === 404) {
    response = await fetchOptions("/distinct-security-charges");
  }

  if (!response) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Error fetching security charges");
    insertFrontendErrorLog({ responseBody: errorText || "Error fetching security charges" });
    throw new Error("Error fetching security charges");
  }

  const data = await response.json();
  return Array.isArray(data)
    ? data.filter((charge) => charge !== null && charge !== undefined && String(charge).trim() !== "")
    : [];
}

export const fetchPortfolioRebalance = async (token, requestBody) => {
  insertFrontendLog({ requestBody });
  const response = await fetch(`${process.env.REACT_APP_API_URL}/rebalance/portfolio`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(requestBody)
  });

  if (handleForbidden(response)) return;

  // // const traceID = response.headers.get("X-Trace-ID");

  if (response.status === 400) {
    // Return a special object for 400 error
    return { status: 400 };
  }
  if (!response.ok) {
    const errorText = await response.text();
    insertFrontendErrorLog({ responseBody: errorText || response.status })
    throw new Error(`API error: ${errorText || response.status}`);
  }

  const data = await response.json();

  // Log request and response to server
  insertFrontendLog({ responseBody: data })

  return data;
};

// Poll for an async rebalance result by requestId (backup path when the
// WebSocket result was missed — e.g. the user navigated away or reloaded).
// Returns null while the result is still unavailable (404), so callers can keep
// polling. The caller must also gate on isTerminalRebalanceResult because the
// endpoint returns a pending acknowledgment until computation finishes.
export const fetchRebalanceStatus = async (token, requestId) => {
  if (!requestId) return null;
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/rebalance/portfolio/status/${requestId}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) return null;

  // Cache entry not found yet — treat as still pending.
  if (response.status === 404) return null;

  if (!response.ok) {
    const errorText = await response
      .text()
      .catch(() => "Error fetching rebalance status");
    insertFrontendErrorLog({ responseBody: errorText || "Error fetching rebalance status" });
    throw new Error("Error fetching rebalance status");
  }

  return response.json();
};

// Cancel an in-flight async rebalance by requestId.
// ---------------------------------------------------------------------------
// Uses the backend's delete endpoint for rebalance processing so the UI can
// explicitly cancel an in-flight request. The call is best-effort / non-fatal:
// any failure (including a 404 if the backend is not yet updated) is swallowed,
// since the local job state is removed regardless.
// ---------------------------------------------------------------------------
export const cancelPortfolioRebalance = async (token, requestId) => {
  if (!requestId) return { success: false };
  try {
    insertFrontendLog({ requestBody: { cancelRequestId: requestId } });
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/rebalance/portfolio/status/${requestId}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (handleForbidden(response)) return { success: false };

    if (!response.ok) {
      console.warn(`[rebalance] cancel endpoint returned ${response.status}`);
      return { success: false, status: response.status };
    }

    return await response.json().catch(() => ({ success: true }));
  } catch (error) {
    console.warn("[rebalance] cancel request failed", error);
    return { success: false };
  }
};

export const fetchPortfolioRecalculate = async (token, requestBody) => {
  insertFrontendLog({ requestBody });
  const response = await fetch(`${process.env.REACT_APP_API_URL}/rebalance/recalculate-portfolio-summary`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(requestBody)
  });

  if (handleForbidden(response)) return;

  // const traceID = response.headers.get("X-Trace-ID");

  if (response.status === 400) {
    // Return a special object for 400 error
    return { status: 400 };
  }
  if (!response.ok) {
    const errorText = await response.text();
    insertFrontendErrorLog({ responseBody: errorText || response.status });
    throw new Error(`API error: ${errorText || response.status}`);
  }

  const data = await response.json();

  // Log request and response to server
  insertFrontendLog({ responseBody: data });

  return data;
};

export const fetchPortfolioPlayRecalculate = async (token, requestBody) => {
  insertFrontendLog({ requestBody });
  const response = await fetch(`${process.env.REACT_APP_API_URL}/rebalance/recalculate-portfolio-play`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(requestBody)
  });

  if (handleForbidden(response)) return;

  // const traceID = response.headers.get("X-Trace-ID");

  if (response.status === 400) {
    return { status: 400 };
  }
  if (!response.ok) {
    const errorText = await response.text();
    insertFrontendErrorLog({ responseBody: errorText || response.status });
    throw new Error(`API error: ${errorText || response.status}`);
  }

  const data = await response.json();

  // Log request and response to server
  insertFrontendLog({ responseBody: data });
  return data;
};

export const placeBulkOrdersPortfolioRebalance = async (token, orders) => {
  insertFrontendLog({ orders });
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/rebalance-orders`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(orders),
    }
  );

  if (handleForbidden(response)) return;
  if (response.status === 400) {
    insertFrontendErrorLog({ responseBody: "Something went wrong." });
    throw new Error("Something went wrong.");
  }
  if (!response.ok) {
    const errorText = await response.text();
    insertFrontendErrorLog({ responseBody: errorText || response.status });
    throw new Error(`Error placing bulk orders: ${errorText}`);
  }

  const data = await response.json();

  // Check for success: false in response
  if (data.success === false) {
    insertFrontendErrorLog({ responseBody: data.message || "Error placing bulk orders" });
    throw new Error(data.message || "Error placing bulk orders");
  }

  insertFrontendLog({ responseBody: data });

  return data;
};

export const fetchSnpRatingOptions = async (token) => {
  
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/inventory/snpRatingList`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );
  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Error fetching S&P rating options");
    insertFrontendErrorLog({ responseBody: errorText || "Error fetching S&P rating options" });
    throw new Error("Error fetching S&P rating options");
  }
  return response.json();
};

export const fetchMoodyRatingOptions = async (token) => {
  
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/inventory/moodyRatingList`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );
  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Error fetching Moody's rating options");
    insertFrontendErrorLog({ responseBody: errorText || "Error fetching Moody's rating options" });
    throw new Error("Error fetching Moody's rating options");
  }
  return response.json();
};

export const fetchYieldMatrix2D = async (token) => {
  
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/inventory/yield-matrix-2d`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );
  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Error fetching yield matrix");
    insertFrontendErrorLog({ responseBody: errorText || "Error fetching yield matrix" });
    throw new Error("Error fetching yield matrix");
  }
  return response.json();
};

export async function fetchBondLadderOrders(token, bondLadderId) {
  const url = `${process.env.REACT_APP_API_URL}/bond-ladder-orders/${bondLadderId}`;
  insertFrontendLog({ requestBody: { bondLadderId } });
  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json"
    }
  });
  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Failed to fetch ladder orders");
    insertFrontendErrorLog({ responseBody: errorText || "Failed to fetch ladder orders" });
    throw new Error("Failed to fetch ladder orders");
  }
  return await response.json();
}

export const fetchFixSessionConfig = async (token, page = 0, size = 10, sortBy = "id", sortDirection = "DESC") => {
  const url = `${process.env.REACT_APP_API_URL}/fix-session-config?page=${page}&size=${size}&sortBy=${sortBy}&sortDirection=${sortDirection}`;
  insertFrontendLog({ requestBody: { page, size, sortBy, sortDirection } });
  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json"
    }
  });
  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Failed to fetch FIX session config");
    insertFrontendErrorLog({ responseBody: errorText || "Failed to fetch FIX session config" });
    throw new Error("Failed to fetch FIX session config");
  }
  return await response.json();
};

export const fetchFixUserConfig = async (token, page = 0, size = 10, sortBy = "id", sortDirection = "DESC") => {
  const url = `${process.env.REACT_APP_API_URL}/fix-user-config?page=${page}&size=${size}&sortBy=${sortBy}&sortDirection=${sortDirection}`;
  insertFrontendLog({ requestBody: { page, size, sortBy, sortDirection } });
  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json"
    }
  });
  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Failed to fetch FIX user config");
    insertFrontendErrorLog({ responseBody: errorText || "Failed to fetch FIX user config" });
    throw new Error("Failed to fetch FIX user config");
  }
  return await response.json();
};

export const fetchEdiFileProcessDetails = async (
  token,
  page = 0,
  size = 20,
  columnToSort = "processingDateTime",
  sortingDirection = "desc",
  filters = {}
) => {
  const params = new URLSearchParams({
    page: page.toString(),
    size: size.toString(),
    columnToSort,
    sortingDirection,
  });
  if (filters.processingStartDateTime) {
    params.append("processingStartDateTime", filters.processingStartDateTime);
  }
  if (filters.processingEndDateTime) {
    params.append("processingEndDateTime", filters.processingEndDateTime);
  }
  const url = `${process.env.REACT_APP_API_URL}/edi-processed/files?${params.toString()}`;
  insertFrontendLog({
    requestBody: {
      page,
      size,
      columnToSort,
      sortingDirection,
      ...filters,
    },
  });
  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json"
    }
  });
  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Failed to fetch EDI file processing details");
    insertFrontendErrorLog({ responseBody: errorText || "Failed to fetch EDI file processing details" });
    throw new Error("Failed to fetch EDI file processing details");
  }
  return await response.json();
};

export const retryEdiFileProcess = async (token, payload) => {
  const url = `${process.env.REACT_APP_API_URL}/edi-file-process/retry`;
  insertFrontendLog({ requestBody: payload });
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify(payload)
  });
  if (handleForbidden(response)) return;
  const data = await response.json().catch(() => null);
  if (!response.ok || data?.success !== true) {
    const message = data?.message || "Failed to trigger EDI file process retry";
    insertFrontendErrorLog({ responseBody: message });
    throw new Error(message);
  }
  insertFrontendLog({ responseBody: data });
  return data;
};

export const updateSessionConfig = async (token, payload) => {
  const url = `${process.env.REACT_APP_API_URL}/fix-session-config`;
  insertFrontendLog({ payload });
  const response = await fetch(url, {
    method: "PUT",
    headers: {
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify(payload)
  });
  if (handleForbidden(response)) return;
  if (response.status === 400) {
    insertFrontendErrorLog( { responseBody: "Something went wrong" });
    return { success: false, message: "Something went wrong" };
  }
  if (!response.ok) {
    insertFrontendErrorLog({ responseBody: "Failed to update session config" });
    throw new Error("Failed to update session config");
  }
  const data = await response.json();
  insertFrontendLog({ responseBody: data });
  return data;
};

export const updateFixUserConfig = async (token, userData) => {
  const url = `${process.env.REACT_APP_API_URL}/fix-user-config`;
  insertFrontendLog({ userData });
  const response = await fetch(url, {
    method: "PUT",
    headers: {
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify(userData)
  });
  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text();
    insertFrontendErrorLog({ responseBody: errorText || "Failed to update FIX user config" });
    throw new Error("Failed to update FIX user config");
  }
  const data = await response.json();
  insertFrontendLog({ responseBody: data });
  return data;
};

export const fetchVenues = async (token, page = 0, size = 10, sortBy = "venueMstId", sortDirection = "DESC") => {
  const url = `${process.env.REACT_APP_API_URL}/fix-venues?page=${page}&size=${size}&sortBy=${sortBy}&sortDirection=${sortDirection}`;
  insertFrontendLog({ requestBody: { page, size, sortBy, sortDirection } });
  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json"
    }
  });
  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Failed to fetch venues");
    insertFrontendErrorLog({ responseBody: errorText || "Failed to fetch venues" });
    throw new Error("Failed to fetch venues");
  }
  return await response.json();
};

export const fetchCustomerInfo = async (token, page = 0, size = 10, sortBy = "custId", sortDirection = "DESC") => {
  const url = `${process.env.REACT_APP_API_URL}/customer-info?page=${page}&size=${size}&sortBy=${sortBy}&sortDirection=${sortDirection}`;
  insertFrontendLog({ requestBody: { page, size, sortBy, sortDirection } });
  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json"
    }
  });
  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Failed to fetch customer info");
    insertFrontendErrorLog({ responseBody: errorText || "Failed to fetch customer info" });
    throw new Error("Failed to fetch customer info");
  }
  return await response.json();
};

export const fetchUserInfo = async (token, page = 0, size = 10, sortBy = "userId", sortDirection = "DESC") => {
  const url = `${process.env.REACT_APP_API_URL}/user/user-info?page=${page}&size=${size}&sortBy=${sortBy}&sortDirection=${sortDirection}`;
  insertFrontendLog({ requestBody: { page, size, sortBy, sortDirection } });
  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json"
    }
  });
  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Failed to fetch user info");
    insertFrontendErrorLog({ responseBody: errorText || "Failed to fetch user info" });
    throw new Error("Failed to fetch user info");
  }
  return await response.json();
};

export const fetchModuleGroups = async (token, page = 0, size = 10, sortBy = "groupMasterId", sortDirection = "DESC") => {
  const url = `${process.env.REACT_APP_API_URL}/fix-module-groups?page=${page}&size=${size}&sortBy=${sortBy}&sortDirection=${sortDirection}`;
  insertFrontendLog({ requestBody: { page, size, sortBy, sortDirection } });
  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json"
    }
  });
  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Failed to fetch module groups");
    insertFrontendErrorLog({ responseBody: errorText || "Failed to fetch module groups" });
    throw new Error("Failed to fetch module groups");
  }
  return await response.json();
};

export const updateUserConfig = async (token, payload) => {
  const url = `${process.env.REACT_APP_API_URL}/fix-user-config`;
  insertFrontendLog({ payload });
  const response = await fetch(url, {
    method: "PUT",
    headers: {
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify(payload)
  });
  if (handleForbidden(response)) return;
  if (response.status === 400) {
    insertFrontendErrorLog( { responseBody: "Something went wrong" });
    return { success: false, message: "Something went wrong" };
  }
  if (!response.ok) {
    insertFrontendErrorLog({ responseBody: "Failed to update user config" });
    throw new Error("Failed to update user config");
  }
  const data = await response.json();
  insertFrontendLog({ responseBody: data });
  return data;
};

//update bond characteristic
/**
 * 
 const payload = {
        side: bondCharacteristicData.side,
        account: bondCharacteristicData.account,
        qty: bondCharacteristicData.qty,
        bondType: bondCharacteristicData.bondType,
        type: bondCharacteristicData.bondType,
        comments: bondCharacteristicData.comments || "",
        characteristics: JSON.stringify(filteredCharacteristics),
        characteristicId: bondCharacteristicData.characteristicId
      };

      const result = await updateBondCharacteristic(token, payload); // assumes API
 */

export const updateBondCharacteristic = async (token, characteristicData) => {
  if (!characteristicData.characteristicId) {
    throw new Error("Characteristic ID is required for update");
  }
  insertFrontendLog({ characteristicData });
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/bond-characteristics/modify/${characteristicData.characteristicId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(characteristicData),
    }
  );
  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text();
    insertFrontendErrorLog({ responseBody: errorText || "Error updating bond characteristic" });
    throw new Error("Error updating bond characteristic");
  }
  const data = await response.json();
  // Check for success: false in response
  if (data.success === false) {
    insertFrontendErrorLog({ responseBody: data.message || "Error updating bond characteristic" });
    throw new Error(data.message || "Error updating bond characteristic");
  }
  insertFrontendLog({ responseBody: data });
  return data;
}
export const submitTrade = async (token, tradeData) => {
  insertFrontendLog({ tradeData });
  const response = await fetch(`${process.env.REACT_APP_API_URL}/trade`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(tradeData),
  });

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text();
    insertFrontendErrorLog({ responseBody: errorText || response.status });
    throw new Error("Error submitting trade");
  }

  const data = await response.json();

  // Check for success: false in response
  if (data.success === false) {
    insertFrontendErrorLog({ responseBody: data.message || "Error submitting trade" });
    throw new Error(data.message || "Error submitting trade");
  }

  insertFrontendLog({ responseBody: data });

  return data;
};
export const createCounterparty = async (token, payload) => {
  insertFrontendLog({ payload });
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/counter-parties`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    }
  );

  if (handleForbidden(response)) return;
  const data = await response.json().catch(() => null);
  // Backend returns 200 always; interpret success/message
  if (!response.ok || data?.success === false) {
    const message = data?.message || "Error creating counterparty";
    insertFrontendErrorLog({ responseBody: message });
    throw new Error(message);
  }

  insertFrontendLog({ responseBody: data });
  return data;
};

export const updateCounterparty = async (token, id, payload) => {
  insertFrontendLog({ id, payload });
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/counter-parties/${id}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    }
  );

  if (handleForbidden(response)) return;
  const data = await response.json().catch(() => null);
  // Backend returns 200 always; interpret success/message
  if (!response.ok || data?.success === false) {
    const message = data?.message || "Error updating counterparty";
    insertFrontendErrorLog({ responseBody: message });
    throw new Error(message);
  }
  insertFrontendLog({ responseBody: data });
  return data;
};

export const deleteCounterparty = async (token, id) => {
  insertFrontendLog({ id });
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/counter-parties/${id}`,
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) return;
  const data = await response.json().catch(() => null);
  if (!response.ok || data?.success === false) {
    const message = data?.message || "Error deleting counterparty";
    insertFrontendErrorLog({ responseBody: message });
    throw new Error(message);
  }
  insertFrontendLog({ responseBody: data });
  return data;
};

// new QuickFIX log helpers
const QUICKFIX_TEXT_LIST_KEYS = new Set(["textIncludes", "textExcludes"]);

const normalizeQuickFixFilters = (filters = {}) => {
  const normalized = {};
  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value == null) return;
    if (QUICKFIX_TEXT_LIST_KEYS.has(key)) {
      const arr = (Array.isArray(value) ? value : [`${value}`])
        .map((item) => `${item}`.trim())
        .filter(Boolean);
      if (arr.length) normalized[key] = arr;
      return;
    }
    normalized[key] = value;
  });
  return normalized;
};

const buildQuickFixBody = (page, size, filters = {}) => ({
  page,
  size,
  ...normalizeQuickFixFilters(filters),
});

export const fetchQuickFixMessages = async (
  token,
  { page = 0, size = 20, filters = {}, signal } = {}
) => {
  const requestBody = buildQuickFixBody(page, size, filters);
  insertFrontendLog({ requestBody });

  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/quickfix/messages/filter`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
      signal,
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Error fetching FIX messages");
    insertFrontendErrorLog({ responseBody: errorText || response.status });
    throw new Error(errorText || "Error fetching FIX messages");
  }

  const data = await response.json();
  insertFrontendLog({ responseBody: data });
  return data;
};

export const fetchQuickFixEvents = async (
  token,
  { page = 0, size = 20, filters = {}, signal } = {}
) => {
  const requestBody = buildQuickFixBody(page, size, filters);
  insertFrontendLog({ requestBody });

  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/quickfix/events/filter`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
      signal,
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Error fetching FIX events");
    insertFrontendErrorLog({ responseBody: errorText || "Error fetching FIX events" });
    throw new Error(errorText || "Error fetching FIX events");
  }

  const data = await response.json();
  insertFrontendLog({ responseBody: data });
  return data;
};

export const fetchFixSessionHealth = async (token) => {
  
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/fix-session/health`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Error fetching session status");
    insertFrontendErrorLog({ responseBody: errorText || "Error fetching session status" });
    throw new Error(errorText || "Error fetching session status");
  }

  return response.json();
};

export const checkAccountIdentifier = async (token, accountIdentifier) => {
  insertFrontendLog({ requestBody: { accountIdentifier } });
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/check-account-identifier?accountIdentifier=${encodeURIComponent(accountIdentifier)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) return;
  // if not ok, throw so UI can handle it
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    insertFrontendErrorLog({ responseBody: errorData?.message || "Error checking account identifier" });
    throw new Error(errorData?.message || "Error checking account identifier");
  }

  return response.json();
};

export const cancelOrder = async (token, orderId, accountId) => {
  insertFrontendLog({ orderId, accountId });
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/order/cancel/${orderId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      // send object body as { accountId: <id> } per backend expectation
      body: accountId,
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    insertFrontendErrorLog({ responseBody: errorData?.message || "Error cancelling order" });
    throw new Error(errorData?.message || "Error cancelling order");
  }

  const data = await response.json();
  insertFrontendLog({ responseBody: data });
  return data;
};
export const fetchSenderCompIds = async (token) => {
  
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/quickfix/sessions/sender-comp-ids`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Error fetching sender comp IDs");
    insertFrontendErrorLog({ responseBody: errorText || "Error fetching sender comp IDs" });
    throw new Error("Error fetching sender comp IDs");
  }

  return response.json();
};

export const fetchTargetCompIds = async (token) => {
  
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/quickfix/sessions/target-comp-ids`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Error fetching target comp IDs");
    insertFrontendErrorLog({ responseBody: errorText || "Error fetching target comp IDs" });
    throw new Error("Error fetching target comp IDs");
  }

  return response.json();
};

export const fetchStrategyParameters = async (token, strategyType) => {
  const url = `${process.env.REACT_APP_API_URL}/strategy/parameters?strategyType=${encodeURIComponent(
    strategyType || ""
  )}`;
  insertFrontendLog({ requestBody: { strategyType } });
  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const message = (await response.text()) || "Error fetching strategy parameters";
    insertFrontendErrorLog({ responseBody: message });
    throw new Error(message);
  }
  const data = await response.json();
  return Array.isArray(data) ? data : [];
};

export const fetchRfqStrategyParams = async (token, rfqId) => {
  const url = `${process.env.REACT_APP_API_URL}/rfq-strategy-params/${rfqId}`;
  insertFrontendLog({ requestBody: { rfqId } });
  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const message = (await response.text()) || "Error fetching RFQ strategy parameters";
    insertFrontendErrorLog({ responseBody: message });
    throw new Error(message);
  }
  const data = await response.json();
  return Array.isArray(data) ? data : [];
};

export const modifyRfq = async (token, rfqId, payload, timeoutRef = null) => {
  if (timeoutRef) {
    clearTimeout(timeoutRef);
  }
  insertFrontendLog({ rfqId, payload });
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/modify-rfq/${rfqId}`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    insertFrontendErrorLog({ responseBody: errorData?.message || "Error updating RFQ" });
    throw new Error(errorData?.message || "Error updating RFQ");
  }

  const data = await response.json();
  if (data.success === false) {
    insertFrontendErrorLog({ responseBody: data.message || "Error updating RFQ" });
    throw new Error(data.message || "Error updating RFQ");
  }
  insertFrontendLog({ responseBody: data });
  return data;
};

export const allocateTrade = async (token, orderId, account, timeoutRef = null) => {
  if (timeoutRef) {
    clearTimeout(timeoutRef);
  }
  insertFrontendLog({ orderId, account });
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/trade/allocation/${orderId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: account,
    }
  );

  if (handleForbidden(response)) return;

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    insertFrontendErrorLog({ responseBody: errorData?.message || "Error allocating trade" });
    throw new Error(errorData?.message || "Error allocating trade");
  }

  const data = await response.json();
  if (data.success === false) {
    insertFrontendErrorLog({ responseBody: data.message || "Error allocating trade" });
    throw new Error(data.message || "Error allocating trade");
  }
  insertFrontendLog({ responseBody: data });
  return data;
};
export const fetchFixSessions = async (token) => {
  
  const response = await fetch(`${process.env.REACT_APP_API_URL}/quickfix/sessions`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (handleForbidden(response)) return;
  if (!response.ok) {
    insertFrontendErrorLog({ responseBody: `Failed to fetch sessions: ${response.statusText}` });
    throw new Error(`Failed to fetch sessions: ${response.statusText}`);
  }

  return response.json();
};

export const updateAccountOnly = async (token, accountId, description, custodianId) => {
  const payload = {
    accountId,
    description,
    custodianId
  };
  insertFrontendLog({ payload });
  const response = await fetch(`${process.env.REACT_APP_API_URL}/account/update-account`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Failed to update account' }));
    insertFrontendErrorLog({ responseBody: errorData.message || 'Failed to update account' });
    throw new Error(errorData.message || 'Failed to update account');
  }

  const data = await response.json();
  insertFrontendLog({ responseBody: data });
  return data;
};

export const insertFrontendLog = async (logMessage) => {
  try {
    const token = localStorage.getItem('token');
    const response = await fetch(`${process.env.REACT_APP_API_URL}/logs/frontend`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(logMessage)
    });
    if (!response.ok) {
      const errorData = await response.json();
      console.error('Server rejected log:', errorData.message);
      return null;
    }
    return await response.json();
  } catch (error) {
    console.error('Network error while posting log:', error.message);
    return null;
  }
};

export const fetchSecMasterItems = async (token, payload = {}) => {
  // Normalize to the /sec-master-filter contract: { page, size, sortBy, sortDirection }.
  // Callers may still pass the app-internal columnToSort/sortingDirection keys.
  const requestBody = {
    page: 0,
    size: 50,
    sortBy: "identifier",
    sortDirection: "DESC",
    ...payload,
  };
  if (payload.columnToSort) requestBody.sortBy = payload.columnToSort;
  if (payload.sortingDirection) requestBody.sortDirection = payload.sortingDirection;
  if (requestBody.sortDirection) {
    requestBody.sortDirection = String(requestBody.sortDirection).toUpperCase();
  }
  delete requestBody.columnToSort;
  delete requestBody.sortingDirection;

  insertFrontendLog({ requestBody });
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/sec-master-filter`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
    }
  );

  if (handleForbidden(response)) return;

  const data = await response.json();

  if (!response.ok) {
    const errorMsg = data?.message || "Error fetching sec master data";
    insertFrontendErrorLog({ responseBody: errorMsg });
    throw new Error(errorMsg);
  }

  if (data?.success === false) {
    const errorMsg = data?.message || "Error fetching sec master data";
    insertFrontendErrorLog({ responseBody: errorMsg });
    throw new Error(errorMsg);
  }

  insertFrontendLog({ responseBody: data });
  return data;
};

export const downloadSecMasterData = async (token, payload = {}, format = "csv") => {
  const requestBody = { ...payload };
  if (payload.columnToSort) requestBody.sortBy = payload.columnToSort;
  if (payload.sortingDirection) requestBody.sortDirection = String(payload.sortingDirection).toUpperCase();
  delete requestBody.columnToSort;
  delete requestBody.sortingDirection;
  delete requestBody.page;
  delete requestBody.size;

  insertFrontendLog({ requestBody });
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/sec-master/download?format=${encodeURIComponent(format)}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
    }
  );

  if (handleForbidden(response)) return null;

  if (!response.ok) {
    let errorMsg = "Error downloading sec master data";
    try { errorMsg = (await response.json())?.message || errorMsg; } catch { /* ignore */ }
    insertFrontendErrorLog({ responseBody: errorMsg });
    throw new Error(errorMsg);
  }

  return response.blob();
};

// Update a SecMaster record. `payload` contains only the fields the user changed.
export const updateSecurityMaster = async (token, secMasterId, payload) => {
  insertFrontendLog({ secMasterId, payload });
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/sec-master/modify/${secMasterId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    }
  );

  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Error updating security");
    insertFrontendErrorLog({ responseBody: errorText || "Error updating security" });
    throw new Error("Error updating security");
  }

  const data = await response.json();

  // The endpoint nests a second status object under `data.data`.
  if (data?.success === false || data?.data?.success === false) {
    const message = data?.data?.error || data?.message || "Error updating security";
    insertFrontendErrorLog({ responseBody: message });
    throw new Error(message);
  }

  insertFrontendLog({ responseBody: data });
  return data;
};

// Check whether an identifier is available / valid for a new SecMaster record.
// Returns { success: boolean, message: string } from the backend.
export const checkSecurityIdentifier = async (token, identifier) => {
  const url = `${process.env.REACT_APP_API_URL}/sec-master/check-identifier?identifier=${encodeURIComponent(identifier)}`;
  const response = await fetch(url, {
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (handleForbidden(response)) return null;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Error checking identifier");
    throw new Error(errorText || "Error checking identifier");
  }
  return response.json();
};

// Create a new SecMaster record.
export const createSecurityMaster = async (token, payload) => {
  insertFrontendLog({ payload });
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/sec-master/create`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    }
  );
  if (handleForbidden(response)) return;
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Error creating security");
    insertFrontendErrorLog({ responseBody: errorText || "Error creating security" });
    throw new Error("Error creating security");
  }
  const data = await response.json();
  if (data?.success === false || data?.data?.success === false) {
    const message = data?.data?.error || data?.message || "Error creating security";
    insertFrontendErrorLog({ responseBody: message });
    throw new Error(message);
  }
  insertFrontendLog({ responseBody: data });
  return data;
};

export const insertFrontendErrorLog = async (logMessage) => {
  try {
    const token = localStorage.getItem('token');
    const response = await fetch(`${process.env.REACT_APP_API_URL}/logs/frontend/errors`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(logMessage)
    });
    if (!response.ok) {
      const errorData = await response.json();
      console.error('Server rejected error log:', errorData.message);
      return null;
    }
    return await response.json();
  } catch (error) {
    console.error('Network error while posting error log:', error.message);
    return null;
  }
};
